//================================================================
//  Tab: Initial Approvers 
//================================================================

//  Purpose: This tab displays all the Initial Approval approvers

//  Example:
//    <InitialApprovers></InitialApprovers>    

//================================================================

//Libraries
import React, { useState, useEffect } from 'react';

//Contexts

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';
import ReassignApproverPane from '../SidePanes/ReassignApproverPane';

//Functions
import QueryListener from '../../../../../Library/QueryListener';
import ConvertDate from '../../../../../Library/ConvertDate';

//Images
import IconSearch from '../../../../../Components/Images/Icon_Search_Grey.svg';

export default function InitialApprovers() {

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState();

  // Search Input
  const [searchInput, setSearchInput] = useState('');

  // Used to store all initial Approvers 
  const [initialApprovers, setInitialApprovers] = useState([]);

  // Used to store all filtered initial Approvers
  const [initialApproversFiltered, setInitialApproversFiltered] = useState([]);

  // Used to control the visibility of the pane
  const [paneOpen, setPaneOpen] = useState(false);

  // Used to store the selected approver details
  const [selectedApprover, setSelectedApprover] = useState(null);

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Open the Reassign Approver Pane
  function openReassignPane(approver) {
    setSelectedApprover(approver);
    setPaneOpen(true);
  }

  // Close the Reassign Approver Pane
  function closeReassignPane() {
    setPaneOpen(false);
    setSelectedApprover(null);
  }

  // Handles search function
  function handleSearch(value) {

    setSearchInput(value);

    // Reset the Filter
    if (value.length === 0) {

      setInitialApproversFiltered([...initialApproversFiltered]);

    };

    // Search Filter
    //  1. Approver Email
    //  2. Last Modified By
    //  3. Role

    const searchResults = initialApprovers.filter((object) =>
      object?.approveremail?.toLowerCase().includes(value.toLowerCase()) ||
      object?.lastmodifiedby?.email?.toLowerCase().includes(value.toLowerCase())||
      object?.role?.toLowerCase().includes(value.toLowerCase())
    );

    // Save the filtered search results to the useState
    setInitialApproversFiltered(searchResults);

  };

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all initial approvers
  useEffect(() => {

    function onLoadChange(documents) {

      setInitialApprovers(documents);
      setInitialApproversFiltered(documents);

    }

    function onError(error) {

      console.log('error', error);
      setPageStatus('error-fatal');
      
    }

    const unsubscribe = QueryListener('approvers', [
      ['approvalbucket', '==', 'Initial Approval']
    ], onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [])

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      body={
        <div className='flex flex-col px-[15px] gap-[20px]'>


          {/* ----------------------------------- */}
          {/* Initial Approvers Table             */}
          {/* ----------------------------------- */}

          <div className='Table-Container'>

            {/* ======== Sorting and Filters ========== */}
            <div className='flex flex-row justify-between py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

              <label className='font-semibold self-center'>
                Total: {initialApproversFiltered?.length} of {initialApprovers?.length}
              </label>

              {/* Search Bar */}
              <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[300px]'>

                <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                <label htmlFor='searchInput'>
                  <input
                    id='searchInput'
                    className='border-none h-[40px] p-0 m-0 outline-none'
                    type='text'
                    placeholder='Search'
                    onChange={(e) => handleSearch(e.target.value)}
                    value={searchInput}
                  ></input>
                </label>
              </div>

            </div>

            <div className='overflow-x-auto overflow-y-auto rounded-b-lg min-h-[300px] max-h-[calc(100vh-400px)]'>
            <table className='min-w-full table-auto'>
              <thead>
                <tr className='border-b-[#D8D8D8] border-solid border-b-2'>
                  <th className='py-[15px] px-[20px] font-medium'>Email</th>
                  <th className='py-[15px] px-[20px] font-medium'>Role</th>
                  <th className='py-[15px] px-[20px] font-medium'>Last Modified By</th>
                  <th className='py-[15px] px-[20px] font-medium'>Last Modified</th>
                  <th className='py-[15px] px-[20px] font-medium'></th>
                </tr>
              </thead>
              <tbody>
                {
                  // No Initial Approvers found
                  initialApproversFiltered?.length === 0 ?
                    (
                      <tr>
                        <td colSpan={5}>
                          <div className='text-center p-[2%]'>
                            No approvers found.
                          </div>
                        </td>
                      </tr>
                    )
                    :
                    (
                      // Map in each Initial Approvers
                      initialApproversFiltered?.map((approver, index) => (
                        <tr key={index} className='border-b-[#C8C8C9] border-solid border-b hover:bg-[#F0F7F7]'>

                          {/* Email */}
                          <td className='py-[15px] px-[20px]' >
                            <p className='font-medium mb-0'>{approver?.approvergivenname} {approver?.approversurname}</p>
                            <p className='text-[#504f4f] mb-0'>{approver?.approveremail}</p>
                          </td>

                          {/* Role*/}
                          <td className='py-[15px] px-[20px] '>
                            {approver?.role}
                          </td>

                          {/* Last Modified By */}
                          <td className='py-[15px] px-[20px]'>
                            <p className='font-medium mb-0 capitalize'>{approver?.lastmodifiedby?.givenname} {approver?.lastmodifiedby?.surname}</p>
                            <p className='text-[#504f4f] mb-0'>{approver?.lastmodifiedby?.email}</p>
                          </td>

                          {/* Last Modified */}
                          <td className='py-[15px] px-[20px]'>{ConvertDate(approver?.lastmodifieddate)}</td>

                          {/* Reassign */}
                          <td className='py-[15px] px-[20px]'>
                            <button className='Secondary-Button' style={{ color: '#00A7A4', border: '1px solid #00A7A4' }} onClick={() => openReassignPane(approver)}>
                              Reassign
                            </button>
                          </td>

                        </tr>
                      ))
                    )
                }
              </tbody>
            </table>
            </div>

          </div>
          
          {/* Reassign Approver Pane */}
          {
            paneOpen && 
            <ReassignApproverPane
              paneOpen={paneOpen}
              setPaneOpen={setPaneOpen}
              approver={selectedApprover}
              onClose={closeReassignPane}
            ></ReassignApproverPane>
          }

        </div>
      }
    ></PageComponent>
  )
}